import { useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../store";

/**
 * Plugin picker state for one generator tab.
 *
 * - Filters plugins along both axes that matter: output medium (isVideo) and
 *   input requirement (requiresSourceImage vs. the current gallery selection).
 *   Image-input plugins are only offered while images are selected; text-only
 *   plugins only with an empty selection.
 * - Remembers the last used plugin per tab in localStorage so users don't
 *   re-pick on every visit.
 * - Clears the remembered choice with a hint when it becomes incompatible
 *   (e.g. the selection changed from empty to N images).
 */
export function usePluginPicker(storageKey, isVideo) {
  const plugins = useStore((state) => state.pluginData);
  const selectedImages = useStore((state) => state.selectedImages);
  const hasSelection = selectedImages.length > 0;

  const [optionSource, setOptionSourceRaw] = useState(() => {
    try {
      return localStorage.getItem(storageKey);
    } catch {
      return null;
    }
  });
  const [clearedLabel, setClearedLabel] = useState(null);
  // Plugin auto-cleared due to a selection change; restored if/when the
  // selection becomes compatible with it again.
  const [pendingRestoreId, setPendingRestoreId] = useState(null);

  const compatiblePlugins = useMemo(
    () =>
      (plugins ?? []).filter((p) => {
        // Multi-mode (exclusively gated) manifests: offerable on this tab when ANY
        // mode matches the surface and the current gallery-selection state — e.g.
        // a merged studio plugin is pickable with no selection (photo modes) and
        // with a selection (video modes).
        if (p.v2Modes?.length) {
          // Multi-mode manifests: offer on any matching surface. Don't pre-filter
          // by selection state — the form demands a source image per-mode, which
          // is friendlier than making the plugin vanish from the tab.
          const surface = isVideo ? "video" : "image";
          return p.v2Modes.some((m) => !m.media || m.media === surface);
        }
        return !!p.isVideo === isVideo && !!p.requiresSourceImage === hasSelection;
      }),
    [plugins, isVideo, hasSelection]
  );

  const prevSourceRef = useRef(optionSource);
  useEffect(() => {
    const source = optionSource;
    if (
      source &&
      !compatiblePlugins.some((p) => p.id === source) &&
      prevSourceRef.current === source
    ) {
      // Only auto-clear when incompatibility came from a selection change,
      // not from pluginData still loading (empty list would clear wrongly).
      if ((plugins ?? []).length > 0) {
        const label = (plugins ?? []).find((p) => p.id === source)?.pluginTitle;
        setClearedLabel(label ?? null);
        setPendingRestoreId(source);
        try {
          localStorage.removeItem(storageKey);
        } catch {}
        setOptionSourceRaw(null);
      }
    }
    prevSourceRef.current = source;
  }, [compatiblePlugins, optionSource, plugins, storageKey]);

  useEffect(() => {
    if (!pendingRestoreId) return;
    if (compatiblePlugins.some((p) => p.id === pendingRestoreId)) {
      setOptionSourceRaw(pendingRestoreId);
      try {
        localStorage.setItem(storageKey, pendingRestoreId);
      } catch {}
      setPendingRestoreId(null);
      setClearedLabel(null);
    }
  }, [compatiblePlugins, pendingRestoreId, storageKey]);

  const setOptionSource = (id) => {
    setClearedLabel(null);
    // A manual pick supersedes any pending auto-restore.
    setPendingRestoreId(null);
    setOptionSourceRaw(id);
    try {
      if (id) localStorage.setItem(storageKey, id);
      else localStorage.removeItem(storageKey);
    } catch {}
  };

  return { optionSource, setOptionSource, compatiblePlugins, clearedLabel };
}
